import { FC, useEffect, useState } from 'react';
import { Button, Form, Modal } from 'antd';
import { useMethods } from '@lhb/hook';
import { useSelector } from 'react-redux';
// import { useBigdataFn } from '@/common/utils/bigdata';
import { taskClusterBatchOrFastAdd, taskClusterFastApproval } from '@/common/api/expandStore/taskCluster';
import { AssignType } from '@/common/components/business/ExpandStore/ts-config';
import V2Form from '@/common/components/Form/V2Form';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import FormEmployees from '@/common/components/FormBusiness/FormEmployees';
import { isArray, refactorSelection } from '@lhb/func';
import { employeeListPermission } from '@/common/api/employee';
import { LOCXX_TASK_TEMPLATE_CODE } from '@/common/config/locxxExpansion';

/**
 * @Description 商圈详情头部部分-创建拓店任务
 */
const CreateTask: FC<{
  id: number,
  needApproval: boolean,
  expansionTaskAlias: string, // 任务别名
  btnTitle: string,
  btnConfig: any,
  className: string,
  setShowCreateDrawer: any,
  onCreateSuccess: any,
  modelConfigId: string,
  isTaskProcessing: boolean
}> = ({
  id,
  needApproval,
  expansionTaskAlias, // 任务别名
  btnTitle,
  btnConfig,
  className,
  setShowCreateDrawer,
  onCreateSuccess,
  modelConfigId,
  isTaskProcessing = false
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [isLock, setIsLock] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState<any>([]); // 主管指派人选项

  const [form] = Form.useForm();
  // const bigdataFn = useBigdataFn(); // 使用埋点 Hook

  const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);
  const taskTemplateCode = tenantCheckData?.taskTemplateCode || '';
  const taskAssignRoleIds = isArray(tenantCheckData?.taskAssignRoleIds) ? tenantCheckData?.taskAssignRoleIds : [];
  const isAssignTypeMember = tenantCheckData?.taskAssignType === AssignType.Member; // 是否指派成员
  const isAssignTypeTwice = tenantCheckData?.taskAssignType === AssignType.Twice; // 是否指派主管
  const isAssignTypeThreeTimes = tenantCheckData?.taskAssignType === AssignType.ThreeTimes; // 是否三次指派方式

  useEffect(() => {
    isAssignTypeTwice && methods.getLeaderEmployeeOptions();
  }, [isAssignTypeTwice]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open]);

  const methods = useMethods({
    /**
     * @description 获取可指派主管选项
     */
    async getLeaderEmployeeOptions() {
      const params = isArray(taskAssignRoleIds) ? { roleIds: taskAssignRoleIds } : {};
      const { objectList: data } = await employeeListPermission(params);
      setEmployeeOptions(isArray(data) ? data.map((item) => ({
        ...item,
        name: `${item.name}${item.mobile ? ' ' + item.mobile : ''}`
      })) : []);
      if (isAssignTypeTwice && data.length === 1) { // 指派主管且只有一个时默认指定
        form.setFieldValue('approver', data[0].id);
      }
    },
    /**
     * @description 点击发起按钮
     */
    handleCreateTask() {
      // 如果拓店任务进行中，则不发起任务
      if (isTaskProcessing) {
        onCreateSuccess?.();
        return;
      }
      // bigdataFn('d817ab3d-036d-4bc8-beff-781726b9562d', '详情-发起拓店', '点击了详情-发起拓店', { op_block: '开店区域-详情页', op_event: ``, });

      if (taskTemplateCode === LOCXX_TASK_TEMPLATE_CODE) {
        setShowCreateDrawer?.(true);
        return;
      }

      // 是否走审批
      const isApproval = needApproval && ['clusterA', 'clusterB', 'clusterC', 'standardAndClusterA', 'standardAndClusterB'].includes(taskTemplateCode); // 加盟商任务模板时，即使开启也不使用

      if (isAssignTypeThreeTimes) { // 三次指派，不需要指定开发经理或主管，因此不弹框直接发起请求
        if (isApproval) { // 走审批
          taskClusterFastApproval({ clusterId: id, modelConfigId }).then((res) => {
            if (!res) return;
            V2Message.success({
              content: `成功发起开店区域${expansionTaskAlias || '拓店'}审批`,
            });
            methods.onSuccess();
          });
        } else { // 不走审批
          taskClusterBatchOrFastAdd({ clusterId: id }).then((res) => {
            if (!res) return;
            const taskAlias = expansionTaskAlias || '拓店任务';
            V2Message.success({
              content: `成功发起开店区域${taskAlias}，请前往${taskAlias}待指派菜单查看` // 备选区域
            });
            methods.onSuccess();
          });
        }
        return;
      }
      if (isAssignTypeMember || isAssignTypeTwice) { // 直接指派、二次指派，走审批分别需要指定开发经理、开发主管后发起，因此弹框选择人员；不走审批按原先处理展示创建任务抽屉
        if (isApproval) { // 走审批
          setOpen(true);
        } else { // 不走审批
          setShowCreateDrawer?.(true);
        }
        return;
      }
    },
    /**
     * @description 弹框上点击确认发起
     *  目前实现逻辑，只有两种情况下会出现弹选择框，执行到该提交按钮处理：
     *      二次指派+走审批、一次指派+走审批
     */
    handleOk() {
      if (isLock) return;
      form.validateFields().then((values) => {
        setIsLock(true);
        const params = { // 直接指派、二次指派，发起审批参数相同
          modelConfigId,
          clusterId: id,
          clusterManagerId: values?.approver
        };
        taskClusterFastApproval(params).then((res) => {
          if (!res) return;
          V2Message.success({
            content: `成功发起开店区域${expansionTaskAlias || '拓店'}审批`,
          });
          methods.onSuccess();
        }).finally(() => {
          setIsLock(false);
        });
      });
    },
    onSuccess() {
      setOpen(false);
      onCreateSuccess?.();
    }
  });

  return (
    <div className={className || ''}>
      <Button
        className ='inline-block'
        {...btnConfig}
        onClick={methods.handleCreateTask}
      >
        {btnTitle}
      </Button>
      <Modal
        title={btnTitle}
        open={open}
        onCancel={() => setOpen(false)}
        maskClosable={false}
        width={360}
        onOk={methods.handleOk}
      >
        <V2Form form={form}>
          <div>
            {isAssignTypeTwice ? <V2FormSelect
              placeholder='请选择开发主管'
              name='approver'
              label='开发主管'
              options={refactorSelection(employeeOptions)}
              config={{
                showSearch: true,
              }}
              formItemConfig={{ required: needApproval }}
              rules={[{ required: needApproval, message: '请选择开发主管' }]}
            /> : <></>}
            {isAssignTypeMember ? <FormEmployees
              form={form}
              allowClear={true}
              placeholder='请选择开发人员'
              name='approver'
              label='开发人员'
              formItemConfig={{ required: needApproval }}
              rules={[{ required: needApproval, message: '请选择开发人员' }]}
              showUserMobile={true}
            /> : <></>}
          </div>
        </V2Form>
      </Modal>
    </div>
  );
};

export default CreateTask;
